import {
  GridRenderEditCellParams,
  GridRowId,
  GridRowModes,
  GridRowModesModel,
} from '@mui/x-data-grid-pro';

interface IFocusAdjacentEditableCellParams {
  params: Pick<GridRenderEditCellParams, 'api' | 'field' | 'id'>;
  shiftKey: boolean;
  setRowModesModel: (
    newModel: (oldModel: GridRowModesModel) => GridRowModesModel
  ) => void;
}

export const focusPlaneringAdjacentEditableCell = ({
  params,
  shiftKey,
  setRowModesModel,
}: IFocusAdjacentEditableCellParams): void => {
  const editableColumns = params.api.getAllColumns().filter(column => {
    const isSystemField =
      column.field === '__check__' ||
      column.field === '__error' ||
      column.field === 'actions';
    return !isSystemField && Boolean(column.editable);
  });

  const currentIndex = editableColumns.findIndex(
    column => column.field === params.field
  );
  if (currentIndex < 0) return;

  const nextIndex = shiftKey ? currentIndex - 1 : currentIndex + 1;
  if (nextIndex < 0 || nextIndex >= editableColumns.length) return;

  const nextField = editableColumns[nextIndex].field;

  setRowModesModel(prev => ({
    ...prev,
    [params.id]: { mode: GridRowModes.Edit, fieldToFocus: nextField },
  }));

  setTimeout(() => {
    try {
      const nextCellParams = params.api.getCellParams(params.id, nextField);
      if (params.api.isCellEditable(nextCellParams)) {
        params.api.setCellFocus(params.id, nextField);
        params.api.startCellEditMode({
          id: params.id as GridRowId,
          field: nextField,
        });
      }
    } catch {
      // Fall back to DOM focus handling below.
    }

    const rowElement = document.querySelector(`[data-id="${params.id}"]`);
    const nextCellElement = rowElement?.querySelector(
      `[data-field="${nextField}"]`
    ) as HTMLElement | null;
    const nextInput = nextCellElement?.querySelector(
      'input, textarea, [role="textbox"]'
    ) as HTMLElement | null;

    if (nextInput) {
      nextInput.focus();
      if (nextInput instanceof HTMLInputElement) {
        nextInput.select();
      }
      return;
    }

    nextCellElement?.focus();

    requestAnimationFrame(() => {
      const delayedInput = nextCellElement?.querySelector(
        'input, textarea, [role="textbox"]'
      ) as HTMLElement | null;
      if (delayedInput) {
        delayedInput.focus();
        if (delayedInput instanceof HTMLInputElement) {
          delayedInput.select();
        }
      }
    });
  }, 0);
};
